import { useState, type FormEvent } from "react";
import type { Publication } from "../publications.types";
import type { PublicationFormValues } from "./publicationsAdminApi";
import styles from "./PublicationsAdmin.module.css";

type PublicationFormProps = {
  initial?: Publication;
  submitLabel: string;
  onSubmit: (values: PublicationFormValues) => Promise<void>;
  onCancel?: () => void;
};

export const PublicationForm = ({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: PublicationFormProps) => {
  const isEditing = initial !== undefined;
  const [title, setTitle] = useState(initial?.title ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [secondaryLinkText, setSecondaryLinkText] = useState(
    initial?.secondaryLinkText ?? "",
  );
  const [secondaryLinkUrl, setSecondaryLinkUrl] = useState(
    initial?.secondaryLinkUrl ?? "",
  );
  const [pdf, setPdf] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (title.trim() === "" || description.trim() === "") {
      setError("Title and description are required");
      return;
    }
    if (!isEditing && pdf === null) {
      setError("Please choose a PDF to upload");
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        title,
        description,
        secondaryLinkText,
        secondaryLinkUrl,
        pdf,
      });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className={styles.publicationForm} onSubmit={handleSubmit}>
      <label htmlFor="pub-title">Title</label>
      <input
        id="pub-title"
        type="text"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
      />

      <label htmlFor="pub-description">
        Description (use blank lines for paragraphs)
      </label>
      <textarea
        id="pub-description"
        rows={8}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />

      <label htmlFor="pub-link-text">Secondary link text (optional)</label>
      <input
        id="pub-link-text"
        type="text"
        value={secondaryLinkText}
        onChange={(event) => setSecondaryLinkText(event.target.value)}
      />

      <label htmlFor="pub-link-url">Secondary link URL (optional)</label>
      <input
        id="pub-link-url"
        type="text"
        value={secondaryLinkUrl}
        onChange={(event) => setSecondaryLinkUrl(event.target.value)}
      />

      <label htmlFor="pub-pdf">
        {isEditing ? "Replace PDF (optional)" : "PDF file"}
      </label>
      <input
        id="pub-pdf"
        type="file"
        accept="application/pdf"
        onChange={(event) => setPdf(event.target.files?.[0] ?? null)}
      />
      <p className={styles.hint}>
        The thumbnail is generated automatically from the first page.
      </p>

      {error !== null && <p className={styles.error}>{error}</p>}

      <div className={styles.formActions}>
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : submitLabel}
        </button>
        {onCancel !== undefined && (
          <button type="button" className={styles.secondary} onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};
